import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import config from '../config';

const PAGE_SIZE = 25;
const NUMERIC_COLS = new Set(['capacity', 'total_shows']);

function VenueList() {
    const [venues, setVenues]   = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError]     = useState(null);

    // Filters
    const [search, setSearch]           = useState('');
    const [filterStatus, setFilterStatus]   = useState('');
    const [filterType, setFilterType]       = useState('');
    const [filterCountry, setFilterCountry] = useState('');
    const [filterState, setFilterState]     = useState('');
    const [capMin, setCapMin]               = useState('');
    const [capMax, setCapMax]               = useState('');

    // Sort + page
    const [sortCol, setSortCol] = useState('name');
    const [sortDir, setSortDir] = useState('asc');
    const [page, setPage]       = useState(1);

    useEffect(() => {
        document.title = 'SHARC - Venues';
        axios.get(`${config.API_URL}/venues`)
            .then(res => { setVenues(res.data); setLoading(false); })
            .catch(() => { setError('Failed to load venues'); setLoading(false); });
        return () => { document.title = 'SHARC - Shows Archive'; };
    }, []);

    // Unique filter options
    const statuses  = useMemo(() => [...new Set(venues.map(v => v.status).filter(Boolean))].sort(), [venues]);
    const types     = useMemo(() => [...new Set(venues.map(v => v.venue_type).filter(Boolean))].sort(), [venues]);
    const countries = useMemo(() => [...new Set(venues.map(v => v.country).filter(Boolean))].sort(), [venues]);
    const states    = useMemo(() => {
        const src = filterCountry ? venues.filter(v => v.country === filterCountry) : venues;
        return [...new Set(src.map(v => v.state).filter(Boolean))].sort();
    }, [venues, filterCountry]);

    const handleSort = (col) => {
        if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        else { setSortCol(col); setSortDir('asc'); }
        setPage(1);
    };

    const resetPage = () => setPage(1);

    const clearFilters = () => {
        setSearch(''); setFilterStatus(''); setFilterType('');
        setFilterCountry(''); setFilterState('');
        setCapMin(''); setCapMax('');
        setPage(1);
    };

    const hasFilters = search || filterStatus || filterType || filterCountry || filterState || capMin || capMax;

    const filtered = useMemo(() => {
        const q = search.toLowerCase();
        const minCap = capMin !== '' ? Number(capMin) : null;
        const maxCap = capMax !== '' ? Number(capMax) : null;
        return venues.filter(v => {
            if (search && !((v.name && v.name.toLowerCase().includes(q)) || (v.city && v.city.toLowerCase().includes(q)))) return false;
            if (filterStatus  && v.status     !== filterStatus)  return false;
            if (filterType    && v.venue_type !== filterType)     return false;
            if (filterCountry && v.country    !== filterCountry)  return false;
            if (filterState   && v.state      !== filterState)    return false;
            if (minCap !== null && (v.capacity === null || Number(v.capacity) < minCap)) return false;
            if (maxCap !== null && (v.capacity === null || Number(v.capacity) > maxCap)) return false;
            return true;
        });
    }, [venues, search, filterStatus, filterType, filterCountry, filterState, capMin, capMax]);

    const sorted = useMemo(() => {
        return [...filtered].sort((a, b) => {
            const av = NUMERIC_COLS.has(sortCol) ? Number(a[sortCol] ?? 0) : (a[sortCol] ?? '');
            const bv = NUMERIC_COLS.has(sortCol) ? Number(b[sortCol] ?? 0) : (b[sortCol] ?? '');
            const cmp = NUMERIC_COLS.has(sortCol) ? av - bv : String(av).localeCompare(String(bv));
            return sortDir === 'asc' ? cmp : -cmp;
        });
    }, [filtered, sortCol, sortDir]);

    const totalPages = Math.ceil(sorted.length / PAGE_SIZE);
    const paginated  = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    const SortIcon = ({ col }) => {
        if (sortCol !== col) return <span style={S.sortIcon}>↕</span>;
        return <span style={{ ...S.sortIcon, color: '#bb86fc' }}>{sortDir === 'asc' ? '↑' : '↓'}</span>;
    };

    const statusColor = (status) => {
        if (!status) return '#666';
        const s = status.toLowerCase();
        if (s === 'open')   return '#03dac6';
        if (s === 'closed') return '#cf6679';
        return '#ffb74d';
    };

    if (loading) return <div style={S.message}>Loading venues...</div>;
    if (error)   return <div style={S.errMsg}>{error}</div>;

    return (
        <div>
            {/* ── Header ── */}
            <div style={S.header}>
                <h1 style={S.title}>Venues</h1>
                <div style={S.headerRight}>
                    <span style={S.count}>{sorted.length.toLocaleString()} of {venues.length.toLocaleString()}</span>
                    <Link to="/add-venue" style={S.addButton}>+ Add Venue</Link>
                </div>
            </div>

            {/* ── Filters ── */}
            {/* Row 1: venue name search + capacity range */}
            <div style={S.filterBar}>
                <input
                    type="text"
                    placeholder="Search by venue name or city..."
                    value={search}
                    onChange={e => { setSearch(e.target.value); resetPage(); }}
                    style={S.searchInput}
                />
                <span style={S.capLabel}>Capacity</span>
                <input
                    type="number"
                    placeholder="Min"
                    value={capMin}
                    onChange={e => { setCapMin(e.target.value); resetPage(); }}
                    style={S.capInput}
                    min="0"
                />
                <span style={S.capDash}>–</span>
                <input
                    type="number"
                    placeholder="Max"
                    value={capMax}
                    onChange={e => { setCapMax(e.target.value); resetPage(); }}
                    style={S.capInput}
                    min="0"
                />
                {hasFilters && (
                    <button onClick={clearFilters} style={S.clearBtn}>✕ Clear all</button>
                )}
            </div>

            {/* Row 2: dropdowns */}
            <div style={S.filterBar}>
                <select value={filterStatus} onChange={e => { setFilterStatus(e.target.value); resetPage(); }} style={{ ...S.filterSelect, flex: '1 1 140px' }}>
                    <option value="">All Statuses</option>
                    {statuses.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                <select value={filterType} onChange={e => { setFilterType(e.target.value); resetPage(); }} style={{ ...S.filterSelect, flex: '1 1 140px' }}>
                    <option value="">All Types</option>
                    {types.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
                <select value={filterCountry} onChange={e => { setFilterCountry(e.target.value); setFilterState(''); resetPage(); }} style={{ ...S.filterSelect, flex: '1 1 140px' }}>
                    <option value="">All Countries</option>
                    {countries.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                <select value={filterState} onChange={e => { setFilterState(e.target.value); resetPage(); }} style={{ ...S.filterSelect, flex: '1 1 140px' }} disabled={states.length === 0}>
                    <option value="">All States</option>
                    {states.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
            </div>

            {/* ── Pagination ── */}
            {totalPages > 1 && (
                <div style={S.pagination}>
                    <button
                        onClick={() => setPage(p => Math.max(1, p - 1))}
                        disabled={page === 1}
                        style={{ ...S.pageBtn, ...(page === 1 ? S.pageBtnDisabled : {}) }}
                    >← Prev</button>
                    <div style={S.pageNumbers}>
                        {Array.from({ length: totalPages }, (_, i) => i + 1)
                            .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 2)
                            .reduce((acc, p, i, arr) => {
                                if (i > 0 && p - arr[i - 1] > 1) acc.push('...');
                                acc.push(p);
                                return acc;
                            }, [])
                            .map((p, i) => p === '...'
                                ? <span key={`e-${i}`} style={S.ellipsis}>…</span>
                                : <button key={p} onClick={() => setPage(p)} style={{ ...S.pageBtn, ...(p === page ? S.pageBtnActive : {}) }}>{p}</button>
                            )
                        }
                    </div>
                    <button
                        onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                        disabled={page === totalPages}
                        style={{ ...S.pageBtn, ...(page === totalPages ? S.pageBtnDisabled : {}) }}
                    >Next →</button>
                    <span style={S.pageInfo}>
                        {((page - 1) * PAGE_SIZE) + 1}–{Math.min(page * PAGE_SIZE, sorted.length)} of {sorted.length.toLocaleString()}
                    </span>
                </div>
            )}

            {/* ── Table ── */}
            {sorted.length === 0 ? (
                <div style={S.message}>
                    {hasFilters ? 'No venues match your filters.' : 'No venues yet.'}
                </div>
            ) : (
                <div style={S.tableContainer}>
                    <table style={S.table}>
                        <thead>
                            <tr>
                                {[
                                    { col: 'name',        label: 'Venue Name' },
                                    { col: 'city',        label: 'City' },
                                    { col: 'state',       label: 'State' },
                                    { col: 'country',     label: 'Country' },
                                    { col: 'status',      label: 'Status' },
                                    { col: 'venue_type',  label: 'Type' },
                                    { col: 'capacity',    label: 'Capacity' },
                                    { col: 'total_shows', label: 'Shows' },
                                ].map(({ col, label }) => (
                                    <th
                                        key={col}
                                        style={{ ...S.th, cursor: 'pointer', userSelect: 'none' }}
                                        onClick={() => handleSort(col)}
                                    >
                                        {label} <SortIcon col={col} />
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {paginated.map((v, idx) => (
                                <tr
                                    key={v.id}
                                    style={idx % 2 === 0 ? S.trEven : S.trOdd}
                                    onMouseEnter={e => e.currentTarget.style.backgroundColor = '#252525'}
                                    onMouseLeave={e => e.currentTarget.style.backgroundColor = idx % 2 === 0 ? '#1e1e1e' : '#1a1a1a'}
                                >
                                    <td style={S.td}>
                                        <Link to={`/venues/${v.slug || v.id}`} style={S.link}>{v.name}</Link>
                                    </td>
                                    <td style={S.td}>{v.city || '—'}</td>
                                    <td style={S.td}>{v.state || '—'}</td>
                                    <td style={S.td}>{v.country || '—'}</td>
                                    <td style={S.td}>
                                        {v.status
                                            ? <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                <span style={{ ...S.statusDot, backgroundColor: statusColor(v.status) }} />
                                                {v.status}
                                              </span>
                                            : '—'}
                                    </td>
                                    <td style={S.td}>{v.venue_type || '—'}</td>
                                    <td style={{ ...S.td, ...S.numCell }}>{v.capacity ? Number(v.capacity).toLocaleString() : '—'}</td>
                                    <td style={{ ...S.td, ...S.numCell }}>{Number(v.total_shows) || 0}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

const S = {
    header:       { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' },
    title:        { fontSize: '32px', fontWeight: 'bold', color: '#e0e0e0', margin: 0 },
    headerRight:  { display: 'flex', alignItems: 'center', gap: '16px' },
    count:        { fontSize: '13px', color: '#666' },
    addButton:    { backgroundColor: '#bb86fc', color: '#121212', textDecoration: 'none', padding: '8px 18px', borderRadius: '4px', fontWeight: 'bold', fontSize: '14px' },

    filterBar:    { display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '10px', alignItems: 'center' },
    searchInput:  { backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '6px', padding: '9px 14px', fontSize: '14px', color: '#e0e0e0', flex: '1 1 260px', minWidth: '200px', boxSizing: 'border-box' },
    filterSelect: { backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '6px', padding: '9px 12px', fontSize: '13px', color: '#e0e0e0', flex: '0 1 140px', minWidth: '110px', boxSizing: 'border-box' },

    capRow:       { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px', flexWrap: 'wrap' },
    capLabel:     { fontSize: '12px', color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' },
    capInput:     { backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '6px', padding: '8px 12px', fontSize: '13px', color: '#e0e0e0', width: '100px', boxSizing: 'border-box' },
    capDash:      { color: '#555', fontSize: '14px' },
    clearBtn:     { backgroundColor: 'transparent', color: '#888', border: '1px solid #444', borderRadius: '6px', padding: '8px 12px', fontSize: '13px', cursor: 'pointer', whiteSpace: 'nowrap', marginLeft: '4px' },

    pagination:      { display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '12px', flexWrap: 'wrap' },
    pageNumbers:     { display: 'flex', gap: '4px', alignItems: 'center' },
    pageBtn:         { backgroundColor: '#2c2c2c', color: '#e0e0e0', border: '1px solid #444', borderRadius: '4px', padding: '5px 10px', fontSize: '13px', cursor: 'pointer', minWidth: '36px', textAlign: 'center' },
    pageBtnActive:   { backgroundColor: '#bb86fc', color: '#121212', border: '1px solid #bb86fc', fontWeight: 700 },
    pageBtnDisabled: { opacity: 0.35, cursor: 'default' },
    ellipsis:        { color: '#555', padding: '0 4px', fontSize: '13px' },
    pageInfo:        { fontSize: '12px', color: '#666', marginLeft: '8px' },

    tableContainer: { overflowX: 'auto' },
    table:          { width: '100%', borderCollapse: 'collapse', fontSize: '14px' },
    th:             { textAlign: 'left', padding: '10px 14px', color: '#aaa', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '1px solid #333', whiteSpace: 'nowrap' },
    td:             { padding: '10px 14px', color: '#e0e0e0', borderBottom: '1px solid #222', whiteSpace: 'nowrap' },
    trEven:         { backgroundColor: '#1e1e1e' },
    trOdd:          { backgroundColor: '#1a1a1a' },
    link:           { color: '#bb86fc', textDecoration: 'none' },
    numCell:        { textAlign: 'right', color: '#aaa', fontVariantNumeric: 'tabular-nums' },
    sortIcon:       { fontSize: '10px', marginLeft: '4px', color: '#555' },
    statusDot:      { width: '7px', height: '7px', borderRadius: '50%', display: 'inline-block', flexShrink: 0 },
    message:        { textAlign: 'center', color: '#aaa', fontSize: '16px', padding: '60px 0' },
    errMsg:         { textAlign: 'center', color: '#cf6679', fontSize: '16px', padding: '60px 0' },
};

export default VenueList;
